import { useState, useEffect, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ImageUploader } from "@/components/ImageUploader";
import { BulkImageUploader } from "@/components/BulkImageUploader";
import { AnalysisResults } from "@/components/AnalysisResults";
import { BulkAnalysisResults } from "@/components/BulkAnalysisResults";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ThemeProvider } from "@/components/ThemeProvider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface AnalysisData {
  caption: string;
  tags: Array<{ label: string; confidence: number }>;
  colors: string[];
  scene: string;
  ocrText?: string;
  exif?: {
    make?: string;
    model?: string;
    dateTime?: string;
    focalLength?: string;
    iso?: string;
    exposureTime?: string;
    fNumber?: string;
    gps?: { latitude: number; longitude: number };
  };
  quality?: {
    score: number;
    brightness: string;
    sharpness: string;
    composition: string;
  };
  altText: string;
  socialCaption: string;
}

const STORAGE_KEY = 'bulk-analysis-results';

const Index = () => {
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [bulkResults, setBulkResults] = useState<Array<{ file: File; data: AnalysisData }>>([]);
  const [bulkIsAnalyzing, setBulkIsAnalyzing] = useState(false);

  // Load saved bulk results from localStorage on mount
  useEffect(() => {
    const savedResults = localStorage.getItem(STORAGE_KEY);
    if (savedResults) {
      try {
        const parsed = JSON.parse(savedResults);
        // Note: Files cannot be serialized, so we only restore the data
        // Users can re-upload if they want to work with the files again
        setBulkResults(parsed.map((item: any) => ({
          file: new File([], item.fileName || 'unknown'),
          data: item.data
        })));
        toast.success("Restored previous analysis session");
      } catch (error) {
        console.error("Failed to restore saved results:", error);
      }
    }
  }, []);

  // Save bulk results to localStorage whenever they change
  useEffect(() => {
    if (bulkResults.length > 0) {
      const serializable = bulkResults.map(({ file, data }) => ({
        fileName: file.name,
        data
      }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
    }
  }, [bulkResults]);

  // Handle shared analysis URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareToken = params.get('share');
    
    if (shareToken) {
      loadSharedAnalysis(shareToken);
    }
  }, []);

  const loadSharedAnalysis = async (shareToken: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("share-analysis", {
        body: { method: 'GET' }
      });

      if (error) throw error;

      // Fetch with query param
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/share-analysis?token=${shareToken}`
      );

      if (!response.ok) throw new Error('Failed to load shared analysis');

      const sharedData = await response.json();
      setAnalysisData(sharedData.analysis_data);
      setImageUrl(sharedData.image_url);
      toast.success("Loaded shared analysis");
    } catch (error: any) {
      console.error("Error loading shared analysis:", error);
      toast.error("Failed to load shared analysis");
    }
  };

  const handleAnalysisComplete = (data: AnalysisData, url: string) => {
    setAnalysisData(data);
    setImageUrl(url);
  };

  const handleBulkAnalysisComplete = useCallback((results: Array<{ file: File; data: AnalysisData }>) => {
    setBulkResults(prev => [...prev, ...results]);
  }, []);

  const handleRemoveBulkItems = useCallback((indices: number[]) => {
    setBulkResults(prev => {
      const newResults = prev.filter((_, idx) => !indices.includes(idx));
      if (newResults.length === 0) {
        localStorage.removeItem(STORAGE_KEY);
      }
      return newResults;
    });
  }, []);

  const handleReanalyzeBulkItems = useCallback(async (files: File[]) => {
    toast.info("Re-analyzing selected images...");
  }, []);

  return (
    <ThemeProvider defaultTheme="light" storageKey="photo-analyzer-theme">
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        
        <main className="flex-1 container mx-auto px-4 py-6 sm:py-8 max-w-7xl">
          <Tabs defaultValue="single" className="w-full">
            <TabsList className="grid w-full sm:w-[400px] grid-cols-2 mb-6 sm:mb-8">
              <TabsTrigger value="single" className="text-xs sm:text-sm">Single Image</TabsTrigger>
              <TabsTrigger value="bulk" className="text-xs sm:text-sm">Bulk Analysis</TabsTrigger>
            </TabsList>

            <TabsContent value="single" className="space-y-6 sm:space-y-8">
              <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
                <div className="space-y-4">
                  <ImageUploader 
                    onAnalysisComplete={handleAnalysisComplete}
                    isAnalyzing={isAnalyzing}
                    setIsAnalyzing={setIsAnalyzing}
                  />
                </div>

                <div className="space-y-4">
                  <AnalysisResults 
                    data={analysisData}
                    isAnalyzing={isAnalyzing}
                    imageUrl={imageUrl || undefined}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="bulk" className="space-y-6 sm:space-y-8">
              <BulkImageUploader
                onAnalysisComplete={handleBulkAnalysisComplete}
                isAnalyzing={bulkIsAnalyzing}
                setIsAnalyzing={setBulkIsAnalyzing}
              />
              {bulkResults.length > 0 && (
                <BulkAnalysisResults 
                  results={bulkResults}
                  onReanalyze={handleReanalyzeBulkItems}
                  onRemove={handleRemoveBulkItems}
                />
              )}
            </TabsContent>
          </Tabs>
        </main>

        <Footer />
      </div>
    </ThemeProvider>
  );
};

export default Index;
